import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, Phone } from "lucide-react";
import { BRAND } from "../../mock";
import { Logo } from "./Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shell = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-[#08090b]">
    <header className="border-b border-white/10"><div className="container-x flex h-[72px] items-center"><Logo /></div></header>
    <main className="flex flex-1 items-center justify-center px-6 py-16">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111217] p-8 text-center">{children}</div>
    </main>
  </div>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [reference, setReference] = useState(null);
  const [info, setInfo] = useState({});
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setReference(data.reference);
        setInfo({ amount: data.amount, kind: data.kind, service: data.service });
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* retry */ }
      tries.current += 1;
      if (tries.current >= 10) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell>
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-brand" />
          <h1 className="mt-5 font-display text-3xl font-bold text-white">Confirming payment...</h1>
          <p className="mt-2 text-white/55">This only takes a moment.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-paid">
          <CheckCircle2 className="mx-auto h-14 w-14 text-brand" />
          <h1 className="mt-5 font-display text-3xl font-bold text-white">{info.kind === "deposit" ? "Deposit paid" : "Payment received"}</h1>
          <p className="mt-2 text-white/60">{info.kind === "balance"
            ? `Your £${Number(info.amount || 0).toFixed(2)} payment is received, thank you. There's nothing more to pay.`
            : info.kind === "full"
            ? `Your £${Number(info.amount || 0).toFixed(2)} payment for ${info.service || "your service"} is received and your booking is now pending confirmation. We'll email you as soon as it's confirmed. If we can't take the booking, you'll be refunded in full.`
            : `Your £${Number(info.amount || 0).toFixed(2)} deposit is received and your booking is now pending confirmation. We'll email you as soon as it's confirmed. If we can't take the booking, your deposit is refunded in full.`}</p>
          {reference && <p className="mt-5 rounded-lg border border-white/10 bg-[#0b0c0f] py-3 text-sm text-white/60">Reference <span className="font-semibold text-brand" data-testid="payment-reference">{reference}</span></p>}
        </div>
      )}
      {(state === "error" || state === "timeout") && (
        <div data-testid="payment-error">
          <XCircle className="mx-auto h-14 w-14 text-brand" />
          <h1 className="mt-5 font-display text-3xl font-bold text-white">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="mt-2 text-white/60">{state === "timeout" ? "We couldn't confirm your payment yet. If you were charged, we'll confirm by email." : "Your payment didn't go through. You can try again or contact us."}</p>
        </div>
      )}
      <div className="mt-8 flex flex-col gap-3">
        <Link to="/" className="btn-red flex items-center justify-center gap-2 rounded-lg py-3 font-semibold" data-testid="back-home"><ArrowLeft className="h-4 w-4" />Back to EvtTuning</Link>
        <a href={BRAND.phoneHref} className="flex items-center justify-center gap-2 text-sm text-white/60 hover:text-brand"><Phone className="h-4 w-4" />{BRAND.phone}</a>
      </div>
    </Shell>
  );
};

export const PaymentCancel = () => {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  return (
    <Shell>
      <div data-testid="payment-cancelled">
        <XCircle className="mx-auto h-14 w-14 text-white/40" />
        <h1 className="mt-5 font-display text-3xl font-bold text-white">Payment cancelled</h1>
        <p className="mt-2 text-white/60">No money was taken. Your enquiry{ref ? <> (<span className="text-brand">{ref}</span>)</> : ""} is saved, but the mobile booking is unconfirmed until the deposit is paid.</p>
      </div>
      <div className="mt-8 flex flex-col gap-3">
        <Link to="/#book" className="btn-red rounded-lg py-3 font-semibold" data-testid="try-again">Try Again</Link>
        <Link to="/" className="text-sm text-white/60 hover:text-brand">Back to homepage</Link>
      </div>
    </Shell>
  );
};
